/**
 * CartContext — backed by the real /api/client/cart endpoints.
 *
 * Guest cart: items stored in localStorage + x-session-id header.
 * Authenticated cart: server-side, merged from guest on login via /cart/sync.
 */
import { createContext, useReducer, useEffect, useContext, useCallback, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { useAuth } from '../AuthContext'
import { clientApi, resolveItemThumbnail } from 'services/api'

const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })

// ── helpers ────────────────────────────────────────────────────────────────

const getSessionId = () => {
  let sid = localStorage.getItem('cartSessionId')
  if (!sid) {
    sid = uuidv4()
    localStorage.setItem('cartSessionId', sid)
  }
  return sid
}

const normalizeCartItem = (item) => {
  const upsellDiscount = parseFloat(item.upsell_discount_amount) || 0
  const unitPrice = parseFloat(item.price || item.variant_sale_price || item.variant_price || item.sale_price || item.base_price || 0)
  return {
  ...item,
  _id: String(item.product_id ?? item._id ?? item.id),
  productId: Number(item.product_id ?? item._id ?? item.id),
  itemId: item.id,          // cart_items.id (for update/remove)
  title: item.product_name || item.name || item.title || '',
  price: unitPrice,
  originalPrice: unitPrice + upsellDiscount,
  upsellDiscountAmount: upsellDiscount,
  upsellParentProductId: item.upsell_parent_product_id || null,
  upsellDisplayMessage: item.upsell_display_message || null,
  stock: item.variant_stock ?? item.stock_quantity ?? item.stock ?? 0,
  thumbnail: resolveItemThumbnail(item),
  discountPercentage: 0,
  quantity: item.quantity || 1,
  variantId: item.variant_id || null,
  variantCombination: item.variant_combination
    ? (typeof item.variant_combination === 'string'
       ? (() => { try { return JSON.parse(item.variant_combination) } catch { return null } })()
       : item.variant_combination)
    : null,
}}

// ── reducer ────────────────────────────────────────────────────────────────

const cartReducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case 'CART_LOADED':
      return { ...state, cart: payload, isCartLoading: false }
    case 'CART_LOADING':
      return { ...state, isCartLoading: payload }
    case 'CART_ERROR':
      return { ...state, isCartLoading: false }
    case 'RESET_CART':
      return { ...state, cart: [], isCartLoading: false }
    default:
      return state
  }
}

const initialState = { cart: [], isCartLoading: true }
const CartContext = createContext(initialState)

// ── provider ───────────────────────────────────────────────────────────────

const CartProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { isAuthenticated } = useAuth()
  const [state, dispatch] = useReducer(cartReducer, initialState)

  const sessionId = getSessionId()

  /** Fetch cart from server */
  const fetchCart = useCallback(async () => {
    dispatch({ type: 'CART_LOADING', payload: true })
    try {
      const { data } = await clientApi.cart.get(isAuthenticated ? null : sessionId)
      const items = (data?.data || []).map(normalizeCartItem)
      dispatch({ type: 'CART_LOADED', payload: items })
    } catch {
      dispatch({ type: 'CART_ERROR' })
    }
  }, [isAuthenticated, sessionId])

  useEffect(() => { fetchCart() }, [fetchCart])

  const prevAuthenticated = useRef(isAuthenticated)
  useEffect(() => {
    if (isAuthenticated && !prevAuthenticated.current) {
      syncCartOnLogin()
    }
    prevAuthenticated.current = isAuthenticated
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = async (product, variantId = null, options = {}) => {
    const normalizedOptions = typeof options === 'boolean'
      ? { showNotification: options }
      : (options || {})
    const {
      showNotification = true,
      quantity = 1,
      upsellParentProductId = null,
      upsellDiscountAmount = 0,
      upsellDisplayMessage = null,
    } = normalizedOptions

    dispatch({ type: 'CART_LOADING', payload: true })
    try {
      await clientApi.cart.add(
        {
          product_id: Number(product._id || product.id),
          variant_id: variantId || undefined,
          quantity,
          upsell_parent_product_id: upsellParentProductId || undefined,
          upsell_discount_amount: upsellDiscountAmount || undefined,
          upsell_display_message: upsellDisplayMessage || undefined,
        },
        isAuthenticated ? null : sessionId
      )
      await fetchCart()
      showNotification && enqueueSnackbar(`${product.title || product.name} added to cart`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Could not add to cart', { variant: 'error' })
      dispatch({ type: 'CART_ERROR' })
    }
  }

  const updateCartItem = async (itemId, quantity) => {
    try {
      await clientApi.cart.update(itemId, quantity, isAuthenticated ? null : sessionId)
      await fetchCart()
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Update failed', { variant: 'error' })
    }
  }

  const removeFromCart = async (itemId, showNotification = true) => {
    try {
      await clientApi.cart.remove(itemId, isAuthenticated ? null : sessionId)
      await fetchCart()
      showNotification && enqueueSnackbar('Item removed from cart', { variant: 'info' })
    } catch {
      enqueueSnackbar('Remove failed', { variant: 'error' })
    }
  }

  const decrementCartItem = async (productId, variantId = null, showNotification = true) => {
    console.log('=== DECREMENT CART ITEM DEBUG ===')
    console.log('Looking for productId:', productId, 'variantId:', variantId)
    console.log('Current cart items:', state.cart.map(i => ({
      _id: i._id,
      variantId: i.variantId,
      itemId: i.itemId,
      quantity: i.quantity,
      title: i.title
    })))
    
    const item = state.cart.find((i) =>
      String(i._id) === String(productId) &&
      (variantId ? String(i.variantId) === String(variantId) : !i.variantId)
    )
    
    console.log('Found cart item:', item)
    
    if (!item) {
      console.error('Cart item not found!')
      return
    }
    
    if (item.quantity > 1) {
      console.log(`Decrementing quantity from ${item.quantity} to ${item.quantity - 1}`)
      await updateCartItem(item.itemId, item.quantity - 1)
    } else {
      console.log('Removing item from cart')
      await removeFromCart(item.itemId, showNotification)
    }
  }

  const resetCart = async () => {
    try {
      await clientApi.cart.clear(isAuthenticated ? null : sessionId)
    } catch { /* ignore */ }
    dispatch({ type: 'RESET_CART' })
  }

  const syncCartOnLogin = async () => {
    try {
      await clientApi.cart.sync(sessionId)
      // New session after login
      localStorage.removeItem('cartSessionId')
      await fetchCart()
    } catch { /* ignore */ }
  }

  const getFinalPrice = (product) => product?.price || 0

  const getQuantity = (productId, variantId = null) => {
    const item = state.cart.find((i) =>
      String(i._id) === String(productId) &&
      (variantId ? String(i.variantId) === String(variantId) : !i.variantId)
    )
    return item?.quantity || 0
  }

  const totalPrice = state.cart.reduce((acc, item) => acc + item.price * item.quantity, 0)
  const quantity = state.cart.reduce((acc, item) => acc + item.quantity, 0)
  const totalSavings = state.cart.reduce(
    (acc, item) => acc + (item.upsellDiscountAmount || 0) * item.quantity,
    0
  )

  return (
    <CartContext.Provider
      value={{
        cart: state.cart,
        itemIds: state.cart.map((item) => item._id),
        quantity,
        totalPrice,
        totalSavings,
        isCartLoading: state.isCartLoading,
        sessionId,
        addToCart,
        updateCartItem,
        removeFromCart,
        decrementCartItem,
        resetCart,
        fetchCart,
        syncCartOnLogin,
        getQuantity,
        getFinalPrice,
        // Legacy aliases for backward compatibility
        removeWholeProductFromCart: removeFromCart,
        setCartLoadingState: (b) => dispatch({ type: 'CART_LOADING', payload: b }),
      }}
      displayName="Shopping Cart"
    >
      {children}
    </CartContext.Provider>
  )
}

const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) throw new Error('useCart can only be used inside CartProvider')
  return context
}

export { CartProvider, useCart }
